import { useSnackbar } from "notistack";
import { Info, ArrowRight } from "lucide-react";

import { Button } from "@/shared/components/ui/button";

import equifaxLogo from "@/shared/assets/credit_bureau_logos/equifax_logo.svg";
import experianLogo from "@/shared/assets/credit_bureau_logos/experian_logo.svg";
import transunionLogo from "@/shared/assets/credit_bureau_logos/transunion_logo.svg";
import irsLogo from "@/shared/assets/data_provider_logos/irs_logo.png";
import workNumberLogo from "@/shared/assets/data_provider_logos/work_number.png";
import nctueLogo from "@/shared/assets/data_provider_logos/NCTUE_logo.png";
import nmvtisLogo from "@/shared/assets/data_provider_logos/NMVTIS_logo.png";
import {
  ASSET_SEARCH_OPTIONS,
  type AssetSearchOption,
} from "@/pages/lawyer/client-detail/assetSearchOptions";

interface SearchCardExtras {
  title: string;
  description: string;
  poweredBy: React.ReactNode;
}

const LogoImg = ({ src, alt, className }: { src: string; alt: string; className?: string }) => (
  <img src={src} alt={alt} className={`inline-block align-middle ${className || "h-4 w-auto"}`} />
);

const SEARCH_CARD_EXTRAS: Record<string, SearchCardExtras> = {
  "debts-accounts": {
    title: "Search for Accounts and Liabilities",
    description: "Credit cards, loans, mortgages, and more",
    poweredBy: (
      <span className="inline-flex items-center gap-3">
        <LogoImg src={equifaxLogo} alt="Equifax" className="h-3.5" />
        <LogoImg src={experianLogo} alt="Experian" className="h-5" />
        <LogoImg src={transunionLogo} alt="TransUnion" className="h-5 mb-1" />
      </span>
    ),
  },
  "tax-history": {
    title: "Request Tax History",
    description: "10 years of tax information from the IRS",
    poweredBy: <LogoImg src={irsLogo} alt="IRS" className="h-5" />,
  },
  "employment-benefits": {
    title: "Search Employment and Benefits",
    description: "Retirement, life insurance, 401(k), pensions, final pay",
    poweredBy: <LogoImg src={workNumberLogo} alt="Equifax | The Work Number" className="h-5" />,
  },
  "property-records": {
    title: "Search Property Records",
    description: "Deeds, assessed values, ownership history",
    poweredBy: (
      <span className="inline-flex items-center gap-2">
        Property Insights via <LogoImg src={equifaxLogo} alt="Equifax" className="h-3.5" />
      </span>
    ),
  },
  "utilities-phone": {
    title: "Search Utilities and Phone",
    description: "Active service accounts to close or transfer",
    poweredBy: <LogoImg src={nctueLogo} alt="NCTUE" className="h-6" />,
  },
  "vehicle-records": {
    title: "Search Vehicle Records",
    description: "Registered vehicles, title history, liens",
    poweredBy: <LogoImg src={nmvtisLogo} alt="NMVTIS" className="h-7" />,
  },
};

type SearchCard = AssetSearchOption & SearchCardExtras;

const SEARCH_CARDS: SearchCard[] = ASSET_SEARCH_OPTIONS.map((opt) => ({
  ...opt,
  ...SEARCH_CARD_EXTRAS[opt.id],
}));

export default function AssetSearchTab() {
  const { enqueueSnackbar } = useSnackbar();

  const handleStartAll = () => {
    enqueueSnackbar("Create an account to begin asset searches.", {
      variant: "info",
    });
  };

  return (
    <div className="space-y-6">
      <div className="w-full bg-brand-subtle border border-primary rounded-xl px-6 py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Info className="h-5 w-5 flex-shrink-0 text-brand" aria-hidden />
          <div>
            <p className="font-semibold text-base text-primary">Start asset search</p>
            <p className="text-sm text-secondary">Runs all 6 third-party searches below at once</p>
          </div>
        </div>
        <Button variant="brand" onClick={handleStartAll}>
          Start all searches
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {SEARCH_CARDS.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.id}
              className="bg-white rounded-xl border border-primary p-6 flex flex-col gap-6 min-h-[200px]"
            >
              <div className="flex items-start gap-4">
                <div
                  className={`w-10 h-10 rounded-lg ${card.iconBg} flex items-center justify-center flex-shrink-0`}
                >
                  <Icon className={`w-5 h-5 ${card.iconColor}`} aria-hidden />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-primary leading-tight">
                    {card.title}
                  </h3>
                  <p className="text-sm text-secondary mt-2">{card.description}</p>
                </div>
              </div>
              <p className="text-sm text-secondary mt-auto">
                Powered by
                <span className="inline-flex items-center align-middle ml-2">{card.poweredBy}</span>
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
